import { Box, CSSObject, useBoolean, useTheme } from '@wegroup/design-system';
import React from 'react';
import ErrorBoundary from '../../../../../../../app/src/components/ErrorBoundary';
import { FlowsQuestion } from '../../../../../types/FlowsTypes';
import InputSelector from '../../../../input/InputSelector';
import { getHTMLIdForQuestion } from '../../utils/helperFunctions';
import AskLaterQuestionsCheckbox from '../ask-later-questions/AskLaterQuestionsCheckbox';
import useListenForScroll from './hooks/useListenForScroll';
import QuestionGroupQuestionTitle from './QuestionGroupQuestionTitle';
import { useFlowsSelector } from '../../../../../../../common/src/utils/globalVariables';
import { isSocialLoginUrl } from '../../../../../../../app/src/features/authentication/constants/SocialAuth';

interface Props {
  question: FlowsQuestion;
  isTitleHidden?: boolean;
  isFullWidth?: boolean;
  isNextFullWidth?: boolean;
  styling?: CSSObject;
}

/**
 * If some groups should not show the marker, add them here
 */
const excludedMarkerTagList: string[] = [];

const QuestionGroupQuestion: React.FC<React.PropsWithChildren<Props>> = ({
  question,
  isTitleHidden,
  isFullWidth,
  isNextFullWidth,
  styling,
}) => {
  useListenForScroll(question);
  const { space } = useTheme();
  const [isMouseOverQuestion, setIsMouseOverQuestion] = useBoolean();
  const currentTag = question.tag;
  const isViewOnlyMode = useFlowsSelector(
    (state) => state.flows.isViewOnlyModeOn,
  );

  const shouldShowAskLaterMarker = currentTag
    ? !excludedMarkerTagList.includes(currentTag.toLowerCase()) ||
      isViewOnlyMode
    : true;

  return (
    <ErrorBoundary
      hide
      key={`${question.id}${question.index ? question.index : ''}`}
    >
      <Box
        id={getHTMLIdForQuestion(question)}
        pl={'10'}
        pos="relative"
        alignSelf={isTitleHidden ? 'default' : 'flex-end'}
        gridColumn={{
          base: 'span 2',
          tabletS: isFullWidth ? 'span 2' : 'auto',
        }}
        sx={styling}
        // Is current is full width and the next one isnt, add some margin bottom
        // This makes the question flow more clear and gives whitespace breaks
        mb={isNextFullWidth === false && isFullWidth ? '2' : '0'}
        minWidth={0}
        onMouseEnter={setIsMouseOverQuestion.on}
        onMouseLeave={setIsMouseOverQuestion.off}
      >
        {/* CHECKBOX FOR MARKING TO ASK QUESTION LATER */}
        <Box pos="absolute" right={`calc(100% - ${space[8]})`}>
          <AskLaterQuestionsCheckbox
            show={
              isMouseOverQuestion &&
              !isSocialLoginUrl &&
              shouldShowAskLaterMarker &&
              question.index == null
            }
            question={question}
          />
        </Box>

        {/* TITLE */}
        {!isTitleHidden && <QuestionGroupQuestionTitle question={question} />}
        {/* INPUT */}
        <InputSelector question={question} />
      </Box>
    </ErrorBoundary>
  );
};

export default QuestionGroupQuestion;
